import chalk from "chalk";
import { Command } from "commander";
import { existsSync } from "node:fs";
import { join } from "node:path";
import { spawnSync } from "node:child_process";
import { printBootstrapResult, runBootstrap, runBootstrappedSetup } from "./bootstrap.js";
import { findConfig } from "./config.js";
import { printDoctor, runDoctor, runDoctorNoScaffold } from "./doctor.js";
import type { DriftCheckerName } from "./types.js";
import { printFixResult, runAutoFix } from "./fix.js";
import { AVAILABLE_DRIFT_CHECKERS } from "./drift/registry.js";
import { reportConsole, reportExplain, reportQuiet, reportJSON } from "./reporter.js";
import { syncToolConfigs } from "./drift/checkers/tool-configs.js";
import { printUpdateResult, runUpdate } from "./update.js";
import { runSession, printSessionResult } from "./session.js";
import { runPatternCapture, printPatternCaptureResult } from "./pattern/capture.js";
import { runHealth, printHealth, printTokenReport } from "./health.js";
import { runGuidedFlow, runMenu } from "./flow.js";
import { runCodex, printCodexResult, runRepoBrief, printRepoBriefResult } from "./codex/index.js";
import { registerRuleConvertCommand } from "./cli-rule-convert.js";
import { printError } from "./utils/errors.js";

const program = new Command();

import { createRequire } from "node:module";
const _require = createRequire(import.meta.url);
const _pkg = _require("../package.json") as { version: string };

program
  .name("cai")
  .description("CLI engine for CAI · Coherence AI — drift detection, pre-analysis, and targeted sync")
  .version(_pkg.version);

// ── Layer 2: Drift Detection ──
program
  .command("check")
  .description("Detect drift between scaffold files and codebase reality")
  .option("--json", "Output full drift report as JSON")
  .option("--quiet", "Single-line summary only")
  .option("--verbose", "Print detailed drift diagnostics")
  .option("--explain <query>", "Explain matching drift issues by code, file, or message")
  .option("--only <checkers>", "Run only the listed checkers (comma-separated)")
  .option("--skip <checkers>", "Skip the listed checkers (comma-separated)")
  .option("--stale-days <days>", "Override staleness warning threshold in days", Number)
  .option("--stale-commits <count>", "Override staleness warning threshold in commits", Number)
  .option("--incremental", "Only check scaffold files that have uncommitted changes")
  .option("--fast", "Skip slow checkers (staleness) for faster CI runs")
  .option("--min-score <n>", "CI gate: fail if the drift score is below n (0–100)", Number)
  .option("--max-warnings <n>", "CI gate: fail if warnings exceed n", Number)
  .option("--fix", "Run sync to fix any issues found")
  .option("--tokens", "Show token cost per scaffold context file")
  .option("--history", "Show drift score trend over time")
  .action(async (opts) => {
    try {
      if (opts.explain && (opts.json || opts.quiet)) {
        throw new Error("--explain cannot be combined with --json or --quiet");
      }

      // --tokens: show token costs and exit
      if (opts.tokens) {
        const config = findConfig();
        printTokenReport(config);
        return;
      }

      // --history: show trend instead of running a check
      if (opts.history) {
        const config = findConfig();
        const { readHistory, summarizeHistory } = await import("./drift/history.js");
        const entries = readHistory(config.projectRoot);
        const summary = summarizeHistory(entries);

        if (!summary) {
          console.log(chalk.dim(`No drift history yet — run ${chalk.white("cai check")} a few times to build a trend.`));
          return;
        }

        if (opts.json) {
          console.log(JSON.stringify({ entries, summary }, null, 2));
          return;
        }

        const color = (s: number) => s >= 80 ? chalk.green : s >= 50 ? chalk.yellow : chalk.red;
        const arrow = summary.delta === null ? "" :
          summary.delta > 0 ? chalk.green(`▲ +${summary.delta}`) :
          summary.delta < 0 ? chalk.red(`▼ ${summary.delta}`) :
          chalk.dim("◇ no change");

        console.log();
        console.log(chalk.bold(`  Drift score history — ${summary.count} run${summary.count !== 1 ? "s" : ""}`));
        console.log();
        console.log(`  ${chalk.dim("current")}    ${color(summary.current)(`${summary.current}/100`)}  ${arrow}`);
        console.log(`  ${chalk.dim("best")}       ${color(summary.best)(`${summary.best}/100`)}`);
        console.log(`  ${chalk.dim("worst")}      ${color(summary.worst)(`${summary.worst}/100`)}`);
        console.log(`  ${chalk.dim("average")}    ${color(summary.average)(`${summary.average}/100`)}`);
        console.log();
        console.log(`  ${chalk.dim("trend")}      ${chalk.cyan(summary.sparkline)}  ${chalk.dim("(last 30 runs)")}`);

        if (summary.velocity !== null) {
          const v = summary.velocity;
          const vStr = v > 0 ? chalk.green(`+${v}/run`) : v < 0 ? chalk.red(`${v}/run`) : chalk.dim("0/run");
          const label = v < -3 ? chalk.red("⚠ decaying fast") :
                        v < 0  ? chalk.yellow("↓ slowly decaying") :
                        v > 3  ? chalk.green("↑ improving fast") :
                        v > 0  ? chalk.green("↑ slowly improving") :
                        chalk.dim("→ stable");
          console.log(`  ${chalk.dim("velocity")}   ${vStr}  ${label}  ${chalk.dim(`(last ${summary.velocityWindow} runs)`)}`);
        }
        console.log();

        // Show recent runs as a small table
        const recent = entries.slice(-5).reverse();
        console.log(chalk.dim("  Recent runs"));
        for (const e of recent) {
          const when = new Date(e.ts).toISOString().replace("T", " ").slice(0, 16);
          const scoreStr = e.weightedScore !== undefined
            ? `${e.score}/${e.weightedScore}`
            : `${e.score}`;
          const issues = `${e.errors}E ${e.warnings}W`;
          console.log(`    ${chalk.dim(when)}   ${color(e.score)(scoreStr.padEnd(8))}  ${chalk.dim(issues)}`);
        }
        console.log();
        return;
      }

      const config = findConfig();
      const only = parseCheckerList(opts.only);
      const skip = parseCheckerList(opts.skip);
      // --fast skips the staleness checker (most expensive due to git operations)
      const effectiveSkip = opts.fast
        ? [...(skip ?? []), "staleness" as const]
        : skip;
      const { runDriftCheck } = await import("./drift/index.js");
      const report = await runDriftCheck(config, {
        verbose: (opts.verbose || Boolean(opts.explain)) && !opts.json && !opts.quiet,
        staleDays: opts.staleDays,
        staleCommits: opts.staleCommits,
        only,
        skip: effectiveSkip,
        incremental: opts.incremental,
      });

      const isNonInteractive = opts.json || opts.quiet || opts.explain || opts.fix;

      if (opts.json) {
        reportJSON(report);
      } else if (opts.quiet) {
        reportQuiet(report);
      } else if (opts.explain) {
        reportExplain(report, opts.explain);
      } else if (opts.fix) {
        // --fix: auto-fix then sync remaining
        reportConsole(report, { verbose: opts.verbose });
        const fixResult = await runAutoFix(config, { only, skip });
        printFixResult(fixResult);
        const postFixReport = await runDriftCheck(config, { staleDays: opts.staleDays, staleCommits: opts.staleCommits, only, skip });
        if (postFixReport.issues.some((i) => i.severity === "error")) {
          const { runSync } = await import("./sync/index.js");
          await runSync(config, {});
        }
        return;
      } else {
        // Interactive mode: show report, then guide through next steps
        reportConsole(report, { verbose: opts.verbose });
        const hasIssues = report.issues.some((i) => i.severity === "error" || i.severity === "warning");
        if (hasIssues && process.stdout.isTTY) {
          await runGuidedFlow(config, { verbose: opts.verbose });
          return;
        }
      }

      // CI gate: when --min-score/--max-warnings are set, evaluate the report
      // against them and exit non-zero on failure (explicit CI intent — fires
      // regardless of interactivity). Without gate flags, fall back to the
      // historical "fail on error in non-interactive mode" behaviour.
      const hasGateFlags = opts.minScore !== undefined || opts.maxWarnings !== undefined;
      if (hasGateFlags) {
        const { evaluateGate } = await import("./gate.js");
        const gate = evaluateGate(report, {
          minScore: opts.minScore,
          maxWarnings: opts.maxWarnings,
        });
        if (!gate.passed) {
          if (!opts.json) {
            for (const reason of gate.reasons) {
              console.error(`drift gate failed: ${reason}`);
            }
          }
          process.exit(1);
        }
        return;
      }

      const hasErrors = report.issues.some((i) => i.severity === "error");
      if (hasErrors && isNonInteractive) process.exit(1);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Token benchmark (honest, deterministic, no model calls) ──
program
  .command("bench")
  .description("Measure per-turn context tokens: flat (all files) vs CAI (anchor + routed)")
  .option("--json", "Output the benchmark as JSON")
  .option("--budget <n>", "Router budget per task in tokens", Number)
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runBench } = await import("./bench.js");
      const result = runBench(config.projectRoot, config.scaffoldRoot, {
        budget: opts.budget,
      });

      if (opts.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }

      const sign = (n: number) => (n >= 0 ? chalk.green(`${n}%`) : chalk.red(`${n}%`));
      console.log(chalk.bold("\nToken benchmark") + chalk.dim("  (per-turn standing context, deterministic — no model calls)\n"));
      console.log(`  Scaffold files:     ${result.scaffoldFiles}`);
      console.log(`  Flat (all files):   ${result.flatTotalTokens} tokens/turn`);
      console.log(`  Anchor tax (CAI):   ${result.anchorTokens} tokens/turn ${chalk.dim("(always loaded)")}`);
      console.log(`  Router budget:      ${result.budget} tokens/task\n`);
      console.log(chalk.dim("  Task".padEnd(40) + "flat   CAI   saved"));
      for (const t of result.tasks) {
        console.log(
          "  " + t.task.slice(0, 36).padEnd(38) +
          String(t.flatTokens).padStart(5) + "  " +
          String(t.caiTokens).padStart(5) + "  " +
          sign(t.savedPct)
        );
      }
      console.log();
      console.log(chalk.bold(`  Median saving: ${sign(result.medianSavedPct)}`) +
        (result.losses > 0 ? chalk.red(`  (${result.losses} task(s) cost MORE than flat)`) : ""));
      if (result.routedTaskCount < result.tasks.length / 2) {
        console.log(chalk.yellow(
          `  ⚠ Router served real context for only ${result.routedTaskCount}/${result.tasks.length} tasks — ` +
          `the high saving likely reflects unmatched tasks, not genuine routing. Use --budget or pass realistic tasks.`
        ));
      }
      console.log(chalk.dim("  Honest note: measures standing context tokens per turn, not end-to-end task tokens or answer quality.\n"));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("savings")
  .description("Estimate tokens CAI has saved: context (MCP log), bench projection, and rtk shell savings")
  .option("--json", "Output the report as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { computeSavings } = await import("./savings.js");
      const report = computeSavings(config.projectRoot, config.scaffoldRoot);

      if (opts.json) {
        console.log(JSON.stringify(report, null, 2));
        return;
      }

      const k = (n: number) =>
        n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);
      console.log(chalk.bold("\nTokens saved") + chalk.dim("  (three independent estimates — never summed)\n"));
      console.log(
        "  Context (MCP):  " +
          (!report.context.available
            ? chalk.dim("no scaffold context to measure")
            : report.context.projected
              ? `${chalk.green(`~${k(report.context.savedTokens)}`)} ${chalk.dim("projected — first summary read of each file (grows once Claude queries CAI)")}`
              : `${chalk.green(`~${k(report.context.savedTokens)}`)} ${chalk.dim(`measured across ${report.context.queries} queries — sections, not whole files`)}`)
      );
      console.log(
        "  Per turn (bench):" +
          (report.bench.available
            ? ` ${chalk.green(`~${k(report.bench.savedPerTurn)}`)} ${chalk.dim(`(${report.bench.savedPct}% of standing context — projection)`)}`
            : chalk.dim(" unavailable"))
      );
      console.log(
        "  Shell (rtk):    " +
          (report.rtk.available
            ? `${chalk.green(`~${k(report.rtk.savedTokens)}`)} ${chalk.dim(`across ${report.rtk.commands} commands`)}`
            : chalk.dim("rtk not installed or no findings"))
      );
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Layer 1: Pre-analysis Scanner ──
program
  .command("init")
  .description("Scan codebase and generate pre-analysis brief for AI")
  .option("--json", "Output scanner brief as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runScan } = await import("./scanner/index.js");
      const result = await runScan(config, { jsonOnly: opts.json });

      if (opts.json) {
        console.log(JSON.stringify(result, null, 2));
      } else {
        console.log(result);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("setup [dir]")
  .description("Bootstrap .cai scaffold and run first-time setup in one step")
  .option("--force", "Replace an existing .cai directory")
  .option("--dry-run", "Show what setup would create, modify, and install without writing files")
  .option("--minimal", "Create only the .cai scaffold; skip MCP, hooks, learn recording, rules, skills, and AI population")
  .option("--no-hooks", "Do not install Claude Code hooks")
  .option("--no-mcp", "Do not register the MCP server")
  .option("--no-learn", "Do not enable correction recording")
  .option("--claude", "Use Claude Code tool config during first-time population")
  .option("--cursor", "Use Cursor tool config during first-time population")
  .option("--windsurf", "Use Windsurf tool config during first-time population")
  .option("--copilot", "Use GitHub Copilot tool config during first-time population")
  .option("--opencode", "Skip tool-specific config; use AGENTS.md-compatible setup")
  .action((dir, opts) => {
    try {
      const setupArgs = setupScriptArgs(opts);
      const result = runBootstrap({
        targetDir: dir,
        force: opts.force,
        dryRun: opts.dryRun,
        minimal: opts.minimal,
        installHooks: opts.hooks,
        installMcp: opts.mcp,
        enableLearn: opts.learn,
      });
      if (!opts.dryRun && !opts.minimal) {
        runBootstrappedSetup(dir ?? process.cwd(), setupArgs);
        result.setupRan = true;
      }
      printBootstrapResult(result);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("bootstrap [dir]", { hidden: true })
  .description("Install a local .cai scaffold into the target project directory")
  .option("--force", "Replace an existing .cai directory")
  .option("--setup", "Run .cai/setup.sh immediately after bootstrapping")
  .option("--dry-run", "Show what would be bootstrapped without writing files")
  .option("--minimal", "Create only the .cai scaffold")
  .option("--no-hooks", "Do not install Claude Code hooks")
  .option("--no-mcp", "Do not register the MCP server")
  .option("--no-learn", "Do not enable correction recording")
  .action((dir, opts) => {
    try {
      const result = runBootstrap({
        targetDir: dir,
        force: opts.force,
        dryRun: opts.dryRun,
        minimal: opts.minimal,
        installHooks: opts.hooks,
        installMcp: opts.mcp,
        enableLearn: opts.learn,
      });
      if (opts.setup && !opts.dryRun && !opts.minimal) {
        runBootstrappedSetup(dir ?? process.cwd());
        result.setupRan = true;
      }
      printBootstrapResult(result);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

function setupScriptArgs(opts: {
  dryRun?: boolean;
  claude?: boolean;
  cursor?: boolean;
  windsurf?: boolean;
  copilot?: boolean;
  opencode?: boolean;
}): string[] {
  const args: string[] = [];
  if (opts.dryRun) args.push("--dry-run");

  const toolFlags = [
    ["--claude", opts.claude],
    ["--cursor", opts.cursor],
    ["--windsurf", opts.windsurf],
    ["--copilot", opts.copilot],
    ["--opencode", opts.opencode],
  ] as const;
  for (const [flag, enabled] of toolFlags) {
    if (enabled) args.push(flag);
  }
  return args;
}

program
  .command("doctor")
  .description("Inspect scaffold, manifests, tool configs, and workspace topology")
  .option("--json", "Output doctor report as JSON")
  .option("--no-scaffold", "Run a read-only project diagnosis before CAI setup")
  .action((opts) => {
    try {
      const report = opts.scaffold === false
        ? runDoctorNoScaffold(process.cwd())
        : runDoctor(findConfig());
      if (opts.json) {
        console.log(JSON.stringify(report, null, 2));
      } else {
        printDoctor(report);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("update")
  .description("Refresh CAI infrastructure inside the current project without overwriting project-specific scaffold content")
  .option("--setup", "Run .cai/setup.sh after updating")
  .action((opts) => {
    try {
      const config = findConfig();
      const result = runUpdate({
        scaffoldRoot: config.scaffoldRoot,
      });
      if (opts.setup) {
        runBootstrappedSetup(config.projectRoot);
        result.setupRan = true;
      }
      printUpdateResult(result);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("fix")
  .description("Auto-fix drifted scaffold docs (tool configs, pattern index, script docs)")
  .option("--dry-run", "Show which safe fixes would be applied")
  .option("--only <checkers>", "Fix only issues produced by the listed checkers")
  .option("--skip <checkers>", "Skip issues produced by the listed checkers")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const result = await runAutoFix(config, {
        dryRun: opts.dryRun,
        only: parseCheckerList(opts.only),
        skip: parseCheckerList(opts.skip),
      });
      printFixResult(result, { dryRun: opts.dryRun });

      // If issues remain and we're in a TTY, offer to sync
      if (!opts.dryRun && result.remainingIssueCount > 0 && process.stdout.isTTY) {
        await runGuidedFlow(config);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Layer 3: Targeted Sync ──
program
  .command("sync")
  .description("Run drift check, then build targeted prompts for AI to fix flagged files")
  .option("--dry-run", "Show what would be synced without executing")
  .option("--warnings", "Include warning-only files (by default only errors are synced)")
  .option("--export", "Write one prompt file per drifted file into .cai/sync-queue/")
  .option("--format <fmt>", "Prompt format: markdown (default) or xml (recommended for Claude)")
  .option("--model <model>", "Model override for Claude CLI sync (e.g. haiku for lower cost)")
  .option("--file <path>", "Restrict sync to a specific scaffold file (repeatable)", collectRepeatable, [])
  .option("--headless", "Non-interactive: drive the coding agent headlessly and apply fixes automatically (no prompts; for the GUI sidecar)")
  .option("--agent <name>", "Headless agent: claude (default), codex, or copilot. Override fully with CAI_AGENT_CMD")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runSync } = await import("./sync/index.js");
      const format = opts.format === "xml" ? "xml" : "markdown";
      await runSync(config, { dryRun: opts.dryRun, includeWarnings: opts.warnings, export: opts.export, format, model: opts.model, files: opts.file, headless: opts.headless, agent: opts.agent });
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── GUI sidecar launcher ──
program
  .command("gui")
  .description("Launch the CAI desktop sidecar (glanceable drift dashboard) for this project")
  .action(async () => {
    try {
      const config = findConfig(); // resolves project / errors with init guidance
      const { launchGuiWithDownload } = await import("./gui/launch.js");
      const { binary, projectDir } = await launchGuiWithDownload(config.projectRoot);
      console.log(chalk.green("  ✓ CAI sidecar launched"));
      console.log(chalk.dim(`  ${binary}`));
      console.log(chalk.dim(`  project: ${projectDir}`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Layer 4: Patterns ──
const patternCmd = program
  .command("pattern")
  .description("Manage pattern files");

patternCmd
  .command("add <name>")
  .description("Create a new pattern file and add it to the index")
  .action(async (name) => {
    try {
      const config = findConfig();
      const { runPatternAdd } = await import("./pattern/index.js");
      await runPatternAdd(config, name);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("capture")
  .description("Analyze last commit and draft a pattern file automatically")
  .action(async () => {
    try {
      const config = findConfig();
      const result = await runPatternCapture(config);
      printPatternCaptureResult(result);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("share <name>")
  .description("Share a local pattern to your global library (~/.cai/patterns/)")
  .action(async (name) => {
    try {
      const config = findConfig();
      const { addToLibrary, libraryRoot } = await import("./pattern/library.js");
      const { scanProjectModel } = await import("./scanner/manifest.js");
      const patternPath = join(config.scaffoldRoot, "patterns", `${name}.md`);
      const project = scanProjectModel(config.projectRoot);
      const entry = addToLibrary(patternPath, project, config.projectRoot);
      console.log(chalk.green(`✓ Shared ${chalk.cyan(entry.name)} to library`));
      console.log(chalk.dim(`  hash: ${entry.hash}`));
      console.log(chalk.dim(`  ${libraryRoot()}`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("library")
  .description("List all patterns in your global library (use --where for sync setup)")
  .option("--json", "Output as JSON")
  .option("--where", "Show the library path and how to sync it across devices")
  .option("--history <name>", "Show all versions of a specific pattern")
  .action(async (opts) => {
    try {
      const { listLibrary, libraryRoot, libraryStats, listVersions } = await import("./pattern/library.js");

      if (opts.history) {
        const versions = listVersions(opts.history)
          .sort((a, b) => (b.version ?? 1) - (a.version ?? 1));
        if (versions.length === 0) {
          console.log(chalk.dim(`No pattern named "${opts.history}" in your library.`));
          return;
        }
        console.log();
        console.log(chalk.bold(`  ${opts.history} — ${versions.length} version${versions.length !== 1 ? "s" : ""}`));
        console.log();
        for (const v of versions) {
          const versionLabel = v.version !== undefined ? `v${v.version}` : "v1";
          const date = v.createdAt.split("T")[0];
          console.log(`  ${chalk.green(versionLabel.padEnd(5))}  ${chalk.cyan(v.hash)}  ${chalk.dim(date)}`);
          console.log(`         ${chalk.dim(v.description)}`);
        }
        console.log();
        console.log(chalk.dim(`  Install a specific version with: ${chalk.white("cai pattern install <hash>")}`));
        console.log();
        return;
      }

      if (opts.where) {
        const root = libraryRoot();
        const usingEnv = Boolean(process.env.CAI_HOME);
        console.log();
        console.log(chalk.bold(`  Pattern library location`));
        console.log(`  ${chalk.cyan(root)}  ${usingEnv ? chalk.dim("(from CAI_HOME)") : chalk.dim("(default ~/.cai)")}`);
        console.log();
        console.log(chalk.bold(`  Sync across devices`));
        console.log(chalk.dim(`  Point CAI_HOME at a folder your cloud provider syncs:`));
        console.log();
        console.log(chalk.dim(`    # Dropbox`));
        console.log(`    export CAI_HOME=~/Dropbox/.cai`);
        console.log();
        console.log(chalk.dim(`    # iCloud Drive`));
        console.log(`    export CAI_HOME=~/Library/Mobile\\ Documents/com~apple~CloudDocs/.cai`);
        console.log();
        console.log(chalk.dim(`    # Syncthing / Resilio / etc — any synced folder works`));
        console.log(`    export CAI_HOME=~/Sync/.cai`);
        console.log();
        console.log(chalk.dim(`  Add the export to your shell config (.zshrc / .bashrc) and re-open the terminal.`));
        console.log(chalk.dim(`  Patterns are content-hashed, so simultaneous shares from two machines never collide.`));
        console.log();
        return;
      }

      const entries = listLibrary();
      if (opts.json) {
        console.log(JSON.stringify(entries, null, 2));
        return;
      }
      if (entries.length === 0) {
        console.log(chalk.dim(`No patterns in library yet.`));
        console.log(chalk.dim(`  ${libraryRoot()}`));
        console.log(chalk.dim(`  Share one with: ${chalk.white("cai pattern share <name>")}`));
        console.log(chalk.dim(`  Set up cross-device sync: ${chalk.white("cai pattern library --where")}`));
        return;
      }
      const stats = libraryStats();
      console.log(chalk.bold(`\n  CAI pattern library — ${entries.length} pattern${entries.length !== 1 ? "s" : ""}`));
      console.log(chalk.dim(`  ${libraryRoot()}  ·  ${(stats.bytes / 1024).toFixed(1)} KB\n`));
      for (const e of entries) {
        const stack = e.stack ? chalk.dim(`[${e.stack}]`) : "";
        const versionLabel = e.version && e.version > 1 ? chalk.green(`v${e.version}`) : chalk.dim(`v${e.version ?? 1}`);
        console.log(`  ${chalk.cyan(e.hash)}  ${versionLabel}  ${chalk.bold(e.name.padEnd(28))}  ${stack}`);
        console.log(`                  ${chalk.dim(e.description)}`);
      }
      console.log();
      console.log(chalk.dim(`  Sync across devices: ${chalk.white("cai pattern library --where")}`));
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("suggest")
  .description("Suggest patterns from your library that match this project")
  .option("--json", "Output as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { findMatching } = await import("./pattern/matching.js");
      const { scanProjectModel } = await import("./scanner/manifest.js");
      const project = scanProjectModel(config.projectRoot);
      const matches = findMatching(project);

      if (opts.json) {
        console.log(JSON.stringify(matches, null, 2));
        return;
      }
      if (matches.length === 0) {
        console.log(chalk.dim(`No matching patterns in your library.`));
        console.log(chalk.dim(`  Share patterns from other projects with: ${chalk.white("cai pattern share <name>")}`));
        return;
      }
      console.log(chalk.bold(`\n  Suggested patterns for this project — ${matches.length} match${matches.length !== 1 ? "es" : ""}\n`));
      for (const m of matches.slice(0, 10)) {
        console.log(`  ${chalk.cyan(m.entry.hash)}  ${chalk.bold(m.entry.name.padEnd(28))}  ${chalk.green(`score ${m.score}`)}`);
        console.log(`              ${chalk.dim(m.entry.description)}`);
        if (m.reasons.length > 0) {
          console.log(`              ${chalk.dim("→ " + m.reasons.join("  ·  "))}`);
        }
      }
      console.log();
      console.log(chalk.dim(`  Install one with: ${chalk.white("cai pattern install <hash|name>")}`));
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("recurring")
  .description("Find recurring task types in your commit history and draft patterns for them")
  .option("--write", "Write the drafts to .cai/patterns/ instead of just listing them")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { readRecentCommits, clusterCommits } = await import("./pattern/cluster.js");
      const { buildSuggestionDraft } = await import("./pattern/auto-suggest.js");
      const commits = readRecentCommits(config.projectRoot);
      const clusters = clusterCommits(commits);

      if (clusters.length === 0) {
        console.log(chalk.dim(`No recurring task types found in the last 30 days.`));
        console.log(chalk.dim(`  Need at least 3 commits of the same kind for a cluster.`));
        return;
      }

      console.log(chalk.bold(`\n  Recurring task types — ${clusters.length} cluster${clusters.length !== 1 ? "s" : ""}\n`));
      const today = new Date().toISOString().split("T")[0];
      const { writeFileSync, existsSync, mkdirSync } = await import("node:fs");

      for (const c of clusters) {
        console.log(`  ${chalk.cyan(c.taskType.padEnd(20))} ${chalk.green(`${c.commits.length}×`)}  ${chalk.dim(`last seen ${c.lastSeen.split("T")[0]}`)}`);
        if (c.commonFiles.length > 0) {
          console.log(`    ${chalk.dim("recurring files: " + c.commonFiles.slice(0, 3).join(", "))}`);
        }

        if (opts.write) {
          const filename = `recurring-${c.taskType}-${today}.md`;
          const dest = join(config.scaffoldRoot, "patterns", filename);
          if (existsSync(dest)) {
            console.log(`    ${chalk.yellow("⚠")} ${chalk.dim(`skipped: ${filename} already exists`)}`);
          } else {
            mkdirSync(join(config.scaffoldRoot, "patterns"), { recursive: true });
            writeFileSync(dest, buildSuggestionDraft(c, today), "utf8");
            console.log(`    ${chalk.green("✓")} ${chalk.dim(`wrote ${filename}`)}`);
          }
        }
      }
      console.log();
      if (!opts.write) {
        console.log(chalk.dim(`  Run with ${chalk.white("--write")} to draft pattern files for each cluster.`));
        console.log();
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

patternCmd
  .command("install <query>")
  .description("Install a library pattern into this project (by hash or name)")
  .action(async (query) => {
    try {
      const config = findConfig();
      const { findEntry, readEntryContent } = await import("./pattern/library.js");
      const entry = findEntry(query);
      if (!entry) {
        console.error(chalk.red(`No matching pattern in library: ${query}`));
        console.error(chalk.dim(`  Run: ${chalk.white("cai pattern library")} to see what's available`));
        process.exit(1);
      }
      const dest = join(config.scaffoldRoot, "patterns", `${entry.name}.md`);
      const { existsSync, writeFileSync } = await import("node:fs");
      if (existsSync(dest)) {
        console.error(chalk.red(`Pattern already exists: ${dest}`));
        console.error(chalk.dim(`  Delete or rename it first.`));
        process.exit(1);
      }
      writeFileSync(dest, readEntryContent(entry.hash), "utf8");
      console.log(chalk.green(`✓ Installed ${entry.name} → ${dest}`));
      console.log(chalk.dim(`  Run ${chalk.white("cai fix")} to update patterns/INDEX.md`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Git Hook / Live Watcher ──
program
  .command("watch")
  .description("Install post-commit hook OR run live drift watcher (--mode)")
  .option("--uninstall", "Remove the post-commit hook (hook mode only)")
  .option("--auto-fix", "Auto-fix drift after each commit and refresh session context (hook mode)")
  .option("--threshold <score>", "Auto-fix trigger threshold (default: 80)", Number)
  .option("--mode <mode>", "hook (default) installs git post-commit; live runs an event-driven watcher")
  .option("--debounce <ms>", "Per-file debounce in ms (live mode, default: 300)", Number)
  .option("--max-iterations <n>", "Stop after n drift ticks (live mode, default: 0 = unlimited)", Number)
  .option("--idle-stop <minutes>", "Stop after N minutes with no file events (live mode, default: 0 = off)", Number)
  .option("--paths <glob...>", "Glob patterns to watch relative to project root (live mode)")
  .option("--force", "Start live watcher even if a stale PID file is found")
  .action(async (opts) => {
    try {
      const mode = (opts.mode ?? "hook") as "hook" | "live";
      if (mode === "live") {
        const config = findConfig();
        const { runLiveWatch } = await import("./watch-live.js");
        await runLiveWatch(config, {
          debounceMs: opts.debounce,
          maxIterations: opts.maxIterations,
          idleStopMinutes: opts.idleStop,
          paths: opts.paths,
          force: opts.force ?? false,
        });
        return;
      }
      const config = findConfig();
      const { manageHook } = await import("./watch.js");
      await manageHook(config, {
        uninstall: opts.uninstall,
        autoFix: opts.autoFix,
        threshold: opts.threshold,
      });
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("sota-watch")
  .description("Check for new direct-cluster competitors on GitHub")
  .option("--json", "Output the raw result as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runSotaWatch } = await import("./telemetry/sota-watch.js");
      const result = await runSotaWatch({ projectRoot: config.projectRoot });
      if (opts.json) {
        console.log(JSON.stringify(result, null, 2));
        return;
      }
      if (result.competitors.length === 0) {
        console.log(chalk.dim("No direct-cluster competitors found."));
        return;
      }
      console.log(chalk.bold("Direct-cluster competitors (top 10 by stars):"));
      for (const c of result.competitors) {
        console.log(`  ${chalk.cyan(c.repo)}  ${chalk.yellow(`★${c.stars}`)}  ${chalk.dim(c.pushedAt)}`);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Session context ──
program
  .command("session")
  .description("Generate focused session-start prompt from current git state")
  .option("--print", "Print the prompt instead of copying to clipboard")
  .option("--focus <topic>", "Select context files relevant to a specific topic (e.g. auth, api, deploy)")
  .option("--auto", "Hook mode: emit a tiny JSON context block on stdout for Claude Code's UserPromptSubmit hook (silent on errors)")
  .action(async (opts) => {
    try {
      // --auto: hook mode — must be fast, must never fail loud
      if (opts.auto) {
        const { runSessionAuto } = await import("./session-auto.js");
        // findConfig may throw, but we want a silent no-op then
        let projectRoot = process.cwd();
        try {
          projectRoot = findConfig().projectRoot;
        } catch {
          // ignore — runSessionAuto handles missing scaffold itself
        }
        process.stdout.write(runSessionAuto(projectRoot));
        return;
      }

      const config = findConfig();
      const result = await runSession(config, { copy: !opts.print, focus: opts.focus });
      printSessionResult(result);
      if (opts.print) console.log(result.prompt);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Verify (back-pressure for AI agents) ──
program
  .command("verify")
  .description("Run typecheck + build + detected adapters + drift check")
  .option("--json", "Machine-readable output for hook integration")
  .option("--hook", "Hook mode: silent on success, re-engages only on regressions, exits 2")
  .option("--skip-drift", "Skip the drift check step")
  .option("--skip-adapters", "Skip detected language/tool adapter checks")
  .option("--full", "Also run the test suite (highest signal, slower — not in the default fast path)")
  .option("--changed", "Annotate which failures touch your uncommitted changes (does not change the gate)")
  .option("--no-baseline", "Ignore the stored baseline; treat every failure as blocking")
  .option("--accept", "Also evaluate the cai:accept-start/end acceptance-criteria block")
  .option("--accept-file <path>", "Path to the acceptance-criteria file (default: .cai/ACCEPTANCE.md)")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runVerify, printVerifyResult } = await import("./verify.js");
      const { readBaseline, writeBaseline, computeDelta } = await import("./verify-baseline.js");
      const { getHead } = await import("./git.js");

      // In hook mode, Claude Code passes a JSON payload on stdin (session_id,
      // stop_hook_active). Read it up front; defaults are safe when absent.
      let hookInput = { sessionId: "nosession", stopHookActive: false };
      if (opts.hook) {
        const { parseHookStdin } = await import("./verify-hook.js");
        hookInput = parseHookStdin(await readStdin());
      }

      const result = await runVerify(config, {
        skipDrift: opts.skipDrift,
        skipAdapters: opts.skipAdapters,
        runTests: opts.full,
      });

      // Acceptance-criteria gate (opt-in): append objective task-specific checks.
      if (opts.accept || opts.acceptFile) {
        const { runAccept } = await import("./accept.js");
        const accept = await runAccept(config, { acceptPath: opts.acceptFile });
        if (accept.found) {
          result.steps.push(...accept.steps);
          result.passed = result.passed && accept.passed;
        }
      }

      // Regression delta: a step that was passing and is now failing is a regression
      // the hook should re-engage on; a step that was already failing is pre-existing.
      // Computed on the RAW output, before --changed annotation, so the baseline hash
      // stays stable regardless of the changed-file set.
      const useBaseline = opts.baseline !== false;
      const head = await getHead(config.projectRoot);
      const baseline = useBaseline ? readBaseline(config.projectRoot) : null;
      const headMatches = useBaseline && baseline !== null && baseline.head === head;
      const delta = computeDelta(result, baseline, headMatches);
      // Persist the baseline from the RAW result (before --changed annotation), except
      // in --json mode which is a pure read-out. Hook and human paths both update it.
      if (!opts.json) writeBaseline(config.projectRoot, head, result);

      // --changed: annotate which failures touch the agent's uncommitted edits.
      // Additive only — appended to the step output so every renderer surfaces it.
      // Does NOT change what gates (typecheck/build stay global by design). Applied
      // AFTER the baseline write so the persisted hash is the raw compiler output.
      if (opts.changed) {
        const { getChangedFiles } = await import("./git.js");
        const { annotateStep, annotationLabel } = await import("./verify-changed.js");
        const changed = new Set(await getChangedFiles(config.projectRoot));
        for (const step of result.steps) {
          if (step.status !== "failed") continue;
          const label = annotationLabel(annotateStep(step, changed));
          if (label) step.output = `${step.output ?? ""}\n${label.trim()}`;
        }
      }

      if (opts.json) {
        console.log(JSON.stringify({ ...result, delta }, null, 2));
      } else if (opts.hook) {
        // Hook mode: silent on success, terse on regression (output goes back to Claude).
        // Pre-existing failures are surfaced as context but do NOT block — a repo
        // started red shouldn't trap the agent in an endless verify loop.
        const { decideHookAction, readHookState, writeHookState, appendVerifyEvent } = await import("./verify-hook.js");
        const blocked = delta.blocking.length > 0;
        const decision = decideHookAction(readHookState(config.projectRoot), hookInput.sessionId, blocked);
        writeHookState(config.projectRoot, decision.state);
        appendVerifyEvent(config.projectRoot, {
          sessionId: hookInput.sessionId,
          blocked,
          action: decision.action,
          retries: decision.state.retries,
          stopHookActive: hookInput.stopHookActive,
          blockingSteps: delta.blocking.map((b) => b.step.name),
        });

        if (decision.action === "pass") {
          if (delta.preExisting.length > 0) {
            console.error("cai verify: pre-existing failures (not introduced this session, not blocking):");
            console.error(`  ${delta.preExisting.map((s) => s.name).join(", ")}`);
          }
          return;
        }

        if (decision.action === "budget-exhausted") {
          // Retry budget spent — stop re-engaging so the loop is bounded. Exit 0,
          // but tell the agent why the still-failing checks are no longer blocking.
          console.error(decision.note);
          console.error(`  still failing: ${delta.blocking.map((b) => b.step.name).join(", ")}`);
          return;
        }

        console.error("cai verify failed — regressions introduced this session:");
        for (const { step, classification } of delta.blocking) {
          console.error(`\n[${step.name}] ${step.command} (${classification})`);
          if (step.output) console.error(step.output);
        }
        if (delta.preExisting.length > 0) {
          console.error(`\n(pre-existing failures, not blocking: ${delta.preExisting.map((s) => s.name).join(", ")})`);
        }
        process.exit(2);
      } else {
        printVerifyResult(result);
      }

      if (!result.passed && !opts.json && !opts.hook) process.exit(1);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("verify-install-hook")
  .description("Install the Claude Code Stop hook for cai verify (back-pressure)")
  .action(async () => {
    try {
      const config = findConfig();
      const { ensureVerifyHook } = await import("./mcp/install.js");
      const result = ensureVerifyHook(config.projectRoot);
      const msg = result === "installed" ? "installed"
        : result === "merged" ? "merged with existing Stop hooks"
        : "already present";
      console.log(chalk.green(`✓ cai verify Stop hook ${msg}`));
      console.log(chalk.dim(`  After every agent stop, Claude Code will run cai verify.`));
      console.log(chalk.dim(`  If verification fails, the agent re-engages to fix the issue.`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("verify-uninstall-hook")
  .description("Remove the Claude Code Stop hook for cai verify")
  .action(async () => {
    try {
      const config = findConfig();
      const { removeVerifyHook } = await import("./mcp/install.js");
      const removed = removeVerifyHook(config.projectRoot);
      if (removed) {
        console.log(chalk.green(`✓ cai verify Stop hook removed`));
      } else {
        console.log(chalk.dim(`No cai verify Stop hook found in .claude/settings.json`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Predrift hook (P3) ──
program
  .command("predrift-install-hook")
  .description("Install the Claude Code PreToolUse hook for proactive drift context injection")
  .action(async () => {
    try {
      const config = findConfig();
      const { ensurePredriftHook } = await import("./mcp/install.js");
      const { buildPredriftIndex } = await import("./mcp/predrift-index.js");

      // Build the pattern-refs index first
      const count = buildPredriftIndex(config.scaffoldRoot);

      const result = ensurePredriftHook(config.projectRoot);
      const msg = result === "installed" ? "installed"
        : result === "merged" ? "merged with existing PreToolUse hooks"
        : "already present";
      console.log(chalk.green(`✓ CAI predrift hook ${msg}`));
      console.log(chalk.dim(`  Index built: ${count} file path${count !== 1 ? "s" : ""} referenced across scaffold.`));
      console.log(chalk.dim(`  Before each Edit/Write, Claude gets drift context for the file being changed.`));
      console.log(chalk.dim(`  Fires once per session (guidanceOnce throttle). Edit .claude/settings.json to remove.`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("predrift-uninstall-hook")
  .description("Remove the Claude Code PreToolUse hook for drift context injection")
  .action(async () => {
    try {
      const config = findConfig();
      const { removePredriftHook } = await import("./mcp/install.js");
      const removed = removePredriftHook(config.projectRoot);
      if (removed) {
        console.log(chalk.green(`✓ CAI predrift hook removed`));
      } else {
        console.log(chalk.dim(`No cai predrift hook found in .claude/settings.json`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Hook runtime (invoked by Claude Code hooks, not users directly) ──
const hookCmd = program
  .command("hook")
  .description("Hook runtime commands (invoked by Claude Code, not users directly)");

hookCmd
  .command("predrift")
  .description("Inject drift context before Edit/Write (Claude Code PreToolUse hook)")
  .action(async () => {
    try {
      // findConfig may fail outside a project — silently exit 0 so we never block Claude
      let scaffoldRoot: string;
      try {
        scaffoldRoot = findConfig().scaffoldRoot;
      } catch {
        process.exit(0);
      }
      const { runPredriftHook } = await import("./mcp/predrift-index.js");
      await runPredriftHook(scaffoldRoot);
    } catch {
      // Never block Claude on any error
      process.exit(0);
    }
  });

// ── Session auto hook installer ──
program
  .command("session-install-hook")
  .description("Install the Claude Code UserPromptSubmit hook for cai session --auto")
  .action(async () => {
    try {
      const config = findConfig();
      const { ensureSessionAutoHook } = await import("./mcp/install.js");
      const result = ensureSessionAutoHook(config.projectRoot);
      const msg = result === "installed" ? "installed"
        : result === "merged" ? "merged with existing UserPromptSubmit hooks"
        : "already present";
      console.log(chalk.green(`✓ cai session --auto hook ${msg}`));
      console.log(chalk.dim(`  Each user prompt now gets a tiny context block (uncommitted files, recent commits, hot files).`));
      console.log(chalk.dim(`  Edit .claude/settings.json to inspect or remove.`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("session-uninstall-hook")
  .description("Remove the Claude Code UserPromptSubmit hook for cai session --auto")
  .action(async () => {
    try {
      const config = findConfig();
      const { removeSessionAutoHook } = await import("./mcp/install.js");
      const removed = removeSessionAutoHook(config.projectRoot);
      if (removed) {
        console.log(chalk.green(`✓ cai session --auto hook removed`));
      } else {
        console.log(chalk.dim(`No cai session --auto hook found in .claude/settings.json`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Health dashboard ──
program
  .command("health")
  .description("Context quality dashboard — freshness, coverage, recommendations")
  .option("--json", "Output health report as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const report = await runHealth(config);
      if (opts.json) {
        console.log(JSON.stringify(report, null, 2));
      } else {
        printHealth(report);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Heartbeat (cheap staleness pulse) ──
program
  .command("heartbeat")
  .description("Cheap staleness pulse over scaffold files — prints HEARTBEAT_OK or stale files")
  .option("--json", "Output the heartbeat result as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runHeartbeat } = await import("./heartbeat.js");
      const result = await runHeartbeat(config, { json: opts.json });
      if (!result.ok) process.exit(1);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("watchdog")
  .description("Warn when session context fill is high and emit a condense brief")
  .option("--session <id>", "Session id (reads the /tmp/claude-ctx-<id>.json bridge file)")
  .option("--transcript <path>", "Session transcript path for the fallback heuristic")
  .option("--json", "Output the watchdog result as JSON (for hook injection)")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runWatchdog } = await import("./watchdog/index.js");
      const { bridgePath, discoverFreshBridge } = await import("./watchdog/signal.js");
      // Explicit session wins; otherwise auto-discover the active session's
      // bridge file so the GUI gets a live signal with no wiring. The transcript
      // heuristic is only used when explicitly passed (it over-estimates).
      const resolvedBridge = opts.session ? bridgePath(opts.session) : discoverFreshBridge(config.projectRoot);
      runWatchdog(config, {
        bridgePath: resolvedBridge,
        transcriptPath: opts.transcript,
        json: opts.json,
      });
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("gui-server", { hidden: true })
  .description("Long-lived stdio engine for the GUI sidecar (internal — newline-delimited JSON over stdin/stdout)")
  .action(async () => {
    const { runGuiServer } = await import("./gui-server.js");
    await runGuiServer();
  });

program
  .command("statusline")
  .description("Claude Code statusLine provider: prints a status line and writes the live context-fill bridge file the watchdog reads")
  .option("--install", "Register this as Claude Code's statusLine in ~/.claude/settings.json (global)")
  .option("--uninstall", "Remove the CAI statusLine registration")
  .action(async (opts) => {
    try {
      const sl = await import("./watchdog/statusline.js");
      if (opts.install) {
        const { path, already } = sl.installStatusline();
        console.log(already ? `Already installed in ${path}` : `Installed CAI statusLine in ${path}`);
        console.log("Restart Claude Code (or start a new session) for it to take effect.");
        return;
      }
      if (opts.uninstall) {
        const { path, removed } = sl.uninstallStatusline();
        console.log(removed ? `Removed CAI statusLine from ${path}` : `No CAI statusLine found in ${path}`);
        return;
      }
      // Provider mode: read the statusLine JSON payload from stdin.
      let input = "";
      for await (const chunk of process.stdin) input += chunk;
      let payload = {};
      try {
        payload = JSON.parse(input) as object;
      } catch {
        /* no/invalid payload — still print a fallback line */
      }
      process.stdout.write(sl.writeStatuslineBridge(payload, Date.now()));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Event log + timeline ──
program
  .command("log <kind> <message...>")
  .description("Append an event to the scaffold log (kind: decision, risk, todo, note)")
  .action(async (kind, messageParts) => {
    try {
      const config = findConfig();
      const { logEvent, EVENT_KINDS } = await import("./events.js");
      if (!(EVENT_KINDS as readonly string[]).includes(kind)) {
        throw new Error(`Unknown event kind "${kind}". Use one of: ${EVENT_KINDS.join(", ")}`);
      }
      const message = (messageParts as string[]).join(" ").trim();
      if (!message) throw new Error("Event message cannot be empty.");
      const event = logEvent(config, kind as (typeof EVENT_KINDS)[number], message);
      console.log(chalk.green(`✓ Logged ${chalk.cyan(event.kind)}: ${event.message}`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("timeline")
  .description("View the scaffold event log, newest first")
  .option("--kind <kind>", "Filter by kind: decision, risk, todo, note")
  .option("--limit <n>", "Show at most N events", Number)
  .option("--json", "Output the timeline as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runTimeline } = await import("./timeline.js");
      const { EVENT_KINDS } = await import("./events.js");
      if (opts.kind && !(EVENT_KINDS as readonly string[]).includes(opts.kind)) {
        throw new Error(`Unknown event kind "${opts.kind}". Use one of: ${EVENT_KINDS.join(", ")}`);
      }
      runTimeline(config, { kind: opts.kind, limit: opts.limit, json: opts.json });
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Structural suggestions (deterministic improvement engine) ──
program
  .command("suggest-structure")
  .description("Deterministic scaffold improvement opportunities (overlap, hotspots, islands)")
  .option("--json", "Output suggestions as JSON")
  .option("--brief <n>", "Print a delegate-able apply-brief for the nth suggestion (1-based)", Number)
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { runSuggest } = await import("./suggest/index.js");
      const report = await runSuggest(config);

      if (opts.brief !== undefined) {
        const suggestion = report.suggestions[opts.brief - 1];
        if (!suggestion) {
          console.error(chalk.red(`No suggestion #${opts.brief} (got ${report.suggestions.length}).`));
          process.exit(1);
        }
        const { buildSuggestionBrief } = await import("./suggest/brief.js");
        console.log(buildSuggestionBrief(suggestion, config.projectRoot));
        return;
      }

      if (opts.json) {
        console.log(JSON.stringify(report, null, 2));
        return;
      }

      if (report.suggestions.length === 0) {
        console.log(chalk.dim(`No structural suggestions — scaffold looks well-organized (${report.filesScanned} files scanned).`));
        return;
      }

      console.log(chalk.bold(`\n  Structural suggestions — ${report.suggestions.length} found`) + chalk.dim(`  (${report.filesScanned} files scanned)\n`));
      report.suggestions.forEach((s, i) => {
        console.log(`  ${chalk.cyan(`${i + 1}.`)} ${chalk.bold(s.title)}  ${chalk.dim(`[${s.kind}]`)}`);
        console.log(`      ${chalk.dim(s.rationale)}`);
        console.log(`      ${chalk.dim(`~${s.estimatedSaving} tokens saved (rough ranking estimate)`)}`);
      });
      console.log();
      console.log(chalk.dim(`  Apply one with: ${chalk.white("cai suggest-structure --brief <n>")} | hand the brief to your agent.`));
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Learn (correction recorder) ──
const learnCmd = program
  .command("learn")
  .description("Session recorder — learn from your corrections to Claude (local-only, on by default)");

learnCmd
  .command("enable")
  .description("Start recording user prompts (auto-installs the Claude Code hook)")
  .option("--no-hook", "Skip the Claude Code hook installation")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { enableLearn } = await import("./learn/recorder.js");
      const { ensureLearnHook } = await import("./mcp/install.js");
      enableLearn(config.projectRoot);
      console.log(chalk.green(`✓ Recording enabled`));
      console.log(chalk.dim(`  Prompts will be stored in .cai/.cache/sessions.jsonl (gitignored)`));
      console.log(chalk.dim(`  Nothing leaves your machine. Run ${chalk.white("cai learn forget")} to wipe everything.`));

      if (opts.hook !== false) {
        try {
          const result = ensureLearnHook(config.projectRoot);
          const msg = result === "installed" ? "Claude Code hook installed"
            : result === "merged" ? "Claude Code hook added (merged with existing UserPromptSubmit hooks)"
            : "Claude Code hook already in place";
          console.log(chalk.green(`✓ ${msg}`));
        } catch (err) {
          console.log(chalk.yellow(`⚠ Could not install Claude Code hook automatically: ${(err as Error).message}`));
          console.log(chalk.dim(`  Run ${chalk.white("cai learn install-hook")} later to retry.`));
        }
      } else {
        console.log(chalk.dim(`  Hook install skipped — run ${chalk.white("cai learn install-hook")} when ready.`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("install-hook")
  .description("Install the Claude Code UserPromptSubmit hook for recording")
  .action(async () => {
    try {
      const config = findConfig();
      const { ensureLearnHook } = await import("./mcp/install.js");
      const result = ensureLearnHook(config.projectRoot);
      const msg = result === "installed" ? "installed"
        : result === "merged" ? "merged with existing UserPromptSubmit hooks"
        : "already present";
      console.log(chalk.green(`✓ Claude Code hook ${msg}`));
      console.log(chalk.dim(`  Edit .claude/settings.json to inspect or remove.`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("disable")
  .description("Stop recording and remove the Claude Code hook (existing log is preserved)")
  .action(async () => {
    try {
      const config = findConfig();
      const { disableLearn } = await import("./learn/recorder.js");
      const { removeLearnHook } = await import("./mcp/install.js");
      disableLearn(config.projectRoot);
      const removed = removeLearnHook(config.projectRoot);
      console.log(chalk.green(`✓ Recording disabled`));
      if (removed) {
        console.log(chalk.dim(`  Claude Code hook removed`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("status")
  .description("Show whether recording is active and how many prompts are logged")
  .action(async () => {
    try {
      const config = findConfig();
      const { sessionLogStats } = await import("./learn/recorder.js");
      const stats = sessionLogStats(config.projectRoot);
      const state = stats.enabled ? chalk.green("enabled") : chalk.dim("disabled");
      console.log(`  Recording: ${state}`);
      console.log(chalk.dim(`  Logged prompts: ${stats.entries}`));
      console.log(chalk.dim(`  Log size: ${(stats.bytes / 1024).toFixed(1)} KB`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("forget")
  .description("Delete all recorded prompts (irreversible)")
  .action(async () => {
    try {
      const config = findConfig();
      const { forgetLearn } = await import("./learn/recorder.js");
      const result = forgetLearn(config.projectRoot);
      console.log(chalk.green(`✓ Forgotten`));
      console.log(chalk.dim(`  Deleted ${(result.deletedBytes / 1024).toFixed(1)} KB`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("review")
  .description("Show recurring corrections in your recent prompts")
  .option("--days <n>", "Look back N days (default: 14)", "14")
  .option("--stack <type>", "Only show corrections from prompts in this stack (e.g. package.json, go.mod, current)")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { readPrompts } = await import("./learn/recorder.js");
      const { detectCorrections, clusterCorrections, suggestRule } = await import("./learn/corrections.js");
      const days = parseInt(opts.days, 10);
      const sinceMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const prompts = readPrompts(config.projectRoot, { sinceMs });

      // Resolve --stack current to the actual project stack
      let stackFilter: string | undefined = opts.stack;
      if (stackFilter === "current") {
        const { scanProjectModel } = await import("./scanner/manifest.js");
        stackFilter = scanProjectModel(config.projectRoot).rootManifest?.type ?? undefined;
        if (!stackFilter) {
          console.log(chalk.yellow(`⚠ No manifest detected in this project — falling back to all stacks.`));
        }
      }

      if (prompts.length === 0) {
        console.log(chalk.dim(`No recorded prompts in the last ${days} days.`));
        console.log(chalk.dim(`  Run ${chalk.white("cai learn enable")} to start recording.`));
        return;
      }

      const corrections = detectCorrections(prompts, { stack: stackFilter });
      const clusters = clusterCorrections(corrections);

      const stackLabel = stackFilter ? chalk.dim(` · stack: ${stackFilter}`) : "";
      console.log(chalk.bold(`\n  Session review — last ${days} days`) + stackLabel);
      console.log(chalk.dim(`  ${prompts.length} prompts · ${corrections.length} look like corrections · ${clusters.length} recurring\n`));

      if (clusters.length === 0) {
        console.log(chalk.dim(`  No recurring corrections detected. Either you're not correcting Claude often,`));
        console.log(chalk.dim(`  or each correction is unique. The recorder needs repeats to find patterns.`));
        return;
      }

      console.log(chalk.bold("  Recurring corrections — consider adding these to CLAUDE.md:\n"));
      for (const c of clusters.slice(0, 10)) {
        console.log(`  ${chalk.cyan(c.id)}  ${chalk.green(`${c.count}×`)}  ${c.example}  ${chalk.dim(`[${c.signal}]`)}`);
        console.log(`          ${chalk.dim("→ suggested rule:")} ${chalk.white(suggestRule(c))}`);
      }
      console.log();
      console.log(chalk.dim(`  Apply one with: ${chalk.white("cai learn write-rule <id>")}`));
      console.log(chalk.dim(`  Or write it as a skill: ${chalk.white("cai learn write-skill <id> --provider all")}`));
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("write-rule <id>")
  .description("Append a learned rule to CLAUDE.md under a managed section")
  .option("--days <n>", "Look back N days for the cluster (default: 14)", "14")
  .action(async (id, opts) => {
    try {
      const config = findConfig();
      const { readPrompts } = await import("./learn/recorder.js");
      const { detectCorrections, clusterCorrections, findClusterById, suggestRule, appendLearnedRule } = await import("./learn/corrections.js");
      const { existsSync, readFileSync, writeFileSync } = await import("node:fs");

      const days = parseInt(opts.days, 10);
      const sinceMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const prompts = readPrompts(config.projectRoot, { sinceMs });
      const corrections = detectCorrections(prompts);
      const clusters = clusterCorrections(corrections);
      const cluster = findClusterById(clusters, id);

      if (!cluster) {
        console.error(chalk.red(`No cluster with id "${id}" in the last ${days} days.`));
        console.error(chalk.dim(`  Run ${chalk.white("cai learn review")} to see current cluster ids.`));
        process.exit(1);
      }

      const claudeMdPath = join(config.projectRoot, "CLAUDE.md");
      if (!existsSync(claudeMdPath)) {
        console.error(chalk.red(`CLAUDE.md not found at ${claudeMdPath}`));
        console.error(chalk.dim(`  Run ${chalk.white("cai setup")} first.`));
        process.exit(1);
      }

      const before = readFileSync(claudeMdPath, "utf8");
      const rule = suggestRule(cluster);

      if (before.includes(rule)) {
        console.log(chalk.yellow(`⚠ Rule already present in CLAUDE.md — nothing to do.`));
        return;
      }

      const after = appendLearnedRule(before, rule);
      writeFileSync(claudeMdPath, after, "utf8");

      console.log(chalk.green(`✓ Added to CLAUDE.md`));
      console.log(`  ${chalk.dim("rule:")} ${chalk.white(rule)}`);
      console.log(`  ${chalk.dim("cluster:")} ${cluster.count}× "${cluster.example}"`);
      console.log(chalk.dim(`  Section: <!-- cai:learn-start --> ... <!-- cai:learn-end -->`));
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("write-skill <id>")
  .description("Write a recurring correction as a SKILL.md file")
  .option("--days <n>", "Look back N days for the cluster (default: 14)", "14")
  .option("--stack <type>", "Only use corrections from this stack (e.g. package.json, go.mod, current)")
  .option("--provider <providers>", "Skill target: cai, claude, codex, or all (comma-separated; default: cai)", "cai")
  .option("--personal", "Write to personal provider skill directories instead of project-local directories")
  .option("--overwrite", "Overwrite an existing generated skill")
  .action(async (id, opts) => {
    try {
      const config = findConfig();
      const { readPrompts } = await import("./learn/recorder.js");
      const { detectCorrections, clusterCorrections, findClusterById } = await import("./learn/corrections.js");
      const { parseSkillProviders, writeLearnedSkill } = await import("./learn/skills.js");

      const days = parseInt(opts.days, 10);
      const sinceMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const prompts = readPrompts(config.projectRoot, { sinceMs });

      let stackFilter: string | undefined = opts.stack;
      if (stackFilter === "current") {
        const { scanProjectModel } = await import("./scanner/manifest.js");
        stackFilter = scanProjectModel(config.projectRoot).rootManifest?.type ?? undefined;
        if (!stackFilter) {
          console.log(chalk.yellow(`⚠ No manifest detected in this project — falling back to all stacks.`));
        }
      }

      const corrections = detectCorrections(prompts, { stack: stackFilter });
      const clusters = clusterCorrections(corrections);
      const cluster = findClusterById(clusters, id);

      if (!cluster) {
        console.error(chalk.red(`No cluster with id "${id}" in the last ${days} days.`));
        console.error(chalk.dim(`  Run ${chalk.white("cai learn review")} to see current cluster ids.`));
        process.exit(1);
      }

      const providers = parseSkillProviders(opts.provider);
      const result = writeLearnedSkill(cluster, {
        projectRoot: config.projectRoot,
        scaffoldRoot: config.scaffoldRoot,
        providers,
        scope: opts.personal ? "personal" : "project",
        overwrite: opts.overwrite,
      });

      console.log(chalk.green(`✓ Skill ready: ${chalk.cyan(result.name)}`));
      console.log(`  ${chalk.dim("cluster:")} ${cluster.count}× "${cluster.example}"`);
      for (const target of result.targets) {
        const state = target.written ? chalk.green("wrote") : chalk.dim("exists");
        const provider = `${target.provider}/${target.scope}`;
        console.log(`  ${state} ${chalk.dim(provider.padEnd(16))} ${target.path}`);
      }
      if (result.targets.some((t) => t.skipped)) {
        console.log(chalk.dim(`  Use ${chalk.white("--overwrite")} to replace an existing SKILL.md.`));
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("watch")
  .description("Live tail your session log — get a toast each time you give a recurring correction")
  .action(async () => {
    try {
      const config = findConfig();
      const { isLearnEnabled } = await import("./learn/recorder.js");
      if (!isLearnEnabled(config.projectRoot)) {
        console.log(chalk.yellow(`⚠ Recording is not enabled.`));
        console.log(chalk.dim(`  Run ${chalk.white("cai learn enable")} first.`));
        return;
      }
      const { watchLearnLog } = await import("./learn/watch.js");
      await watchLearnLog({ projectRoot: config.projectRoot });
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

learnCmd
  .command("record")
  .description("Internal: read a prompt from stdin (called by Claude Code's UserPromptSubmit hook)")
  .action(async () => {
    try {
      const config = findConfig();
      const { recordPrompt, isLearnEnabled } = await import("./learn/recorder.js");
      if (!isLearnEnabled(config.projectRoot)) {
        return; // silent no-op when disabled
      }
      // Read all of stdin
      const chunks: Buffer[] = [];
      for await (const chunk of process.stdin) {
        chunks.push(chunk as Buffer);
      }
      const raw = Buffer.concat(chunks).toString("utf8").trim();
      if (!raw) return;

      // Try to parse as Claude Code hook JSON; fall back to raw text
      let prompt = raw;
      try {
        const parsed = JSON.parse(raw) as { prompt?: string; user_prompt?: string };
        prompt = parsed.prompt ?? parsed.user_prompt ?? raw;
      } catch {
        // raw text, that's fine
      }

      // Detect current stack so we can scope correction clusters per tech stack
      let stack: string | undefined;
      try {
        const { scanProjectModel } = await import("./scanner/manifest.js");
        stack = scanProjectModel(config.projectRoot).rootManifest?.type ?? undefined;
      } catch {
        // optional — never fail the hook over a missing manifest
      }

      recordPrompt(config.projectRoot, prompt, { source: "claude-code-hook", stack });
    } catch {
      // never fail the hook
    }
  });

// ── Telemetry stats ──
program
  .command("stats")
  .description("Show MCP query telemetry — which context files Claude actually reads")
  .option("--days <n>", "Look back N days (default: 7)", "7")
  .option("--json", "Output as JSON")
  .action(async (opts) => {
    try {
      const config = findConfig();
      const { readQueries, aggregateByFile, aggregateByTool } = await import("./telemetry/query-log.js");
      const days = parseInt(opts.days, 10);
      const sinceMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const queries = readQueries(config.projectRoot, { sinceMs });

      if (opts.json) {
        console.log(JSON.stringify({
          days,
          totalQueries: queries.length,
          byFile: aggregateByFile(queries),
          byTool: aggregateByTool(queries),
        }, null, 2));
        return;
      }

      if (queries.length === 0) {
        console.log(chalk.dim(`No MCP queries logged in the last ${days} days.`));
        console.log(chalk.dim(`Telemetry starts collecting once Claude calls the cai MCP server.`));
        return;
      }

      console.log(chalk.bold(`\n  CAI telemetry — last ${days} days`));
      console.log(chalk.dim(`  ${queries.length} queries\n`));

      const byTool = aggregateByTool(queries);
      console.log(chalk.bold("  Top tools"));
      for (const t of byTool.slice(0, 5)) {
        console.log(`    ${t.tool.padEnd(28)} ${chalk.cyan(String(t.hits))}`);
      }

      const byFile = aggregateByFile(queries);
      if (byFile.length > 0) {
        console.log(chalk.bold("\n  Hot files"));
        for (const f of byFile.slice(0, 10)) {
          console.log(`    ${f.file.padEnd(40)} ${chalk.cyan(`${f.hits} hits`)}  ${chalk.dim(`~${f.tokens} tokens`)}`);
        }
      }
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Path-scoped Rules ──
program
  .command("rules")
  .description("Generate .claude/rules/ from scaffold context (path-scoped, lazy-loaded)")
  .action(async () => {
    try {
      const config = findConfig();
      const { generateRules, printRulesResult } = await import("./rules.js");
      const result = generateRules(config);
      printRulesResult(result);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── MCP Server ──
const mcpCmd = program
  .command("mcp")
  .description("Start the CAI MCP server for live context queries from Claude");

mcpCmd
  .command("start")
  .description("Start the MCP server (stdio transport — for use via MCP config)")
  .action(async () => {
    try {
      const config = findConfig();
      const { startMcpServer } = await import("./mcp/server.js");
      await startMcpServer(config);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

mcpCmd
  .command("install")
  .description("Print the MCP config snippet to add to Claude Code or Claude Desktop")
  .action(() => {
    try {
      const config = findConfig();
      console.log();
      console.log(chalk.bold("  Add CAI as an MCP server in Claude Code:"));
      console.log();
      console.log(chalk.dim("  In your project's .claude/settings.json (or ~/.claude/settings.json):"));
      console.log();
      console.log(chalk.cyan(`  {
    "mcpServers": {
      "cai": {
        "command": "cai",
        "args": ["mcp", "start"],
        "cwd": "${config.projectRoot}"
      }
    }
  }`));
      console.log();
      console.log(chalk.dim("  Or run in terminal: ") + chalk.white("claude mcp add cai -- cai mcp start"));
      console.log();
      console.log(chalk.dim("  Once installed, Claude can query your scaffold on-demand:"));
      console.log(chalk.dim("  · list_scaffold — see all files with token costs"));
      console.log(chalk.dim("  · search('auth') — find relevant sections without loading full files"));
      console.log(chalk.dim("  · get_file('.cai/context/architecture.md') — targeted file load"));
      console.log(chalk.dim("  · check_drift() — run drift check from within a Claude session"));
      console.log();
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// Default `cai mcp` with no subcommand → show install instructions
mcpCmd.action(() => {
  try {
    console.log();
    console.log(chalk.bold("  cai mcp") + chalk.dim(" — live context queries for Claude"));
    console.log();
    console.log(`  ${chalk.cyan("cai mcp start")}    Start the MCP server (used by Claude automatically)`);
    console.log(`  ${chalk.cyan("cai mcp install")}  Show setup instructions`);
    console.log();
    console.log(chalk.dim("  Quick setup: ") + chalk.white(`claude mcp add cai -- cai mcp start`));
    console.log();
  } catch (err) {
    printError(err);
    process.exit(1);
  }
});

// ── Quick Reference ──
program
  .command("sync-configs")
  .description("Copy the primary tool config to all other tool config files")
  .action(() => {
    try {
      const config = findConfig();
      const result = syncToolConfigs(config.projectRoot);
      if (!result.primary) {
        console.log("No tool config files found.");
        return;
      }
      console.log(`Primary config: ${result.primary}`);
      if (result.updated.length === 0) {
        console.log("No secondary tool config files needed syncing.");
        return;
      }
      console.log("Updated:");
      for (const file of result.updated) {
        console.log(`  ${file}`);
      }
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Rule content converter (cross-provider, no scaffold needed) ──
registerRuleConvertCommand(program);

program
  .command("visualize")
  .description("Launch interactive scaffold graph in the browser")
  .action(() => {
    try {
      const config = findConfig();
      const script = join(config.scaffoldRoot, "visualize.sh");
      if (!existsSync(script)) {
        throw new Error(`visualize.sh not found at ${script}`);
      }
      runScript("bash", [script], config.projectRoot);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("menu")
  .description("Interactive guided menu — check, fix, sync, health")
  .action(async () => {
    try {
      const config = findConfig();
      await runMenu(config);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

// ── Codex ──
program
  .command("codex")
  .description("Generate .cai/codex/modules.md — compact export index for AI context")
  .option("--scan <dirs>", "Comma-separated directories to scan (default: src,lib,utils)")
  .action(async (opts: { scan?: string }) => {
    try {
      const config = findConfig();
      const scanDirs = opts.scan
        ? opts.scan.split(",").map((d: string) => d.trim()).filter(Boolean)
        : undefined;
      const result = await runCodex(config, { scanDirs });
      printCodexResult(result);
      const briefResult = await runRepoBrief(config);
      printRepoBriefResult(briefResult);
    } catch (err) {
      printError(err);
      process.exit(1);
    }
  });

program
  .command("help")
  .description("Show all available commands")
  .action(printHelp);

program
  .command("commands")
  .description("Alias for help")
  .action(printHelp);

program.configureHelp({ sortSubcommands: false, helpWidth: 80 });
program.helpCommand(false);
program.on("command:*", () => {
  console.error(chalk.red(`\n  Unknown command: ${program.args[0]}\n`));
  printHelp();
  process.exit(1);
});

if (process.argv.length === 2) {
  printHelp();
} else {
  program.parse();
}

/**
 * Read all of stdin as a string. Resolves "" if stdin is a TTY (no pipe) or after a
 * short timeout, so `cai verify --hook` never hangs when invoked outside a real hook.
 */
function readStdin(timeoutMs = 200): Promise<string> {
  return new Promise((resolve) => {
    if (process.stdin.isTTY) return resolve("");
    const chunks: Buffer[] = [];
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve(Buffer.concat(chunks).toString("utf8").trim());
    };
    const timer = setTimeout(finish, timeoutMs);
    process.stdin.on("data", (c) => chunks.push(c as Buffer));
    process.stdin.on("end", () => { clearTimeout(timer); finish(); });
    process.stdin.on("error", () => { clearTimeout(timer); finish(); });
  });
}

function runScript(cmd: string, args: string[], cwd: string): void {
  const result = spawnSync(cmd, args, { cwd, stdio: "inherit" });
  if (result.error) throw result.error;
  // 130=Ctrl+C, 143=SIGTERM — not errors
  if (result.status !== null && result.status !== 0 && result.status !== 130 && result.status !== 143) {
    throw new Error(`Process exited with code ${result.status}`);
  }
}

function printHelp() {
  const b = chalk.bold;
  const dim = chalk.dim;
  const cyan = chalk.cyan;
  const w = 24;
  const row = (cmd: string, desc: string) =>
    `  ${cyan(cmd.padEnd(w))}${dim(desc)}`;

  console.log();
  console.log(`  ${b("cai")} ${dim("· Coherence AI")}`);
  console.log();
  console.log(dim("  ─── Getting started ────────────────────────────────────"));
  console.log(row("cai setup", "Bootstrap scaffold + run first-time AI setup"));
  console.log();
  console.log(dim("  ─── Daily workflow ─────────────────────────────────────"));
  console.log(row("cai session", "Smart session-start prompt from current git state"));
  console.log(row("cai session --focus <topic>", "Focus on a topic: auth, api, deploy, db, …"));
  console.log(row("cai check", "Drift score — are scaffold files still accurate?"));
  console.log(row("cai check --fix", "Auto-fix safe issues, then sync remaining"));
  console.log(row("cai check --tokens", "Show token cost per scaffold context file"));
  console.log(row("cai fix", "Auto-fix drifted scaffold docs"));
  console.log(row("cai sync", "Fix drift — AI updates only what's broken"));
  console.log(row("cai sync --export", "Export prompts as .md files for manual AI paste"));
  console.log(row("cai sync --format xml", "Export as XML (recommended for Claude)"));
  console.log(row("cai menu", "Guided menu — check, fix, sync, health in one place"));
  console.log();
  console.log(dim("  ─── Insights ───────────────────────────────────────────"));
  console.log(row("cai health", "Context quality: freshness, coverage, hot files"));
  console.log(row("cai verify", "Back-pressure: typecheck + build + adapters + drift after every agent stop"));
  console.log(row("cai verify --full", "Also run the test suite (highest signal, slower)"));
  console.log(row("cai verify --accept", "Also check the cai:accept-start/end acceptance-criteria block"));
  console.log(row("cai verify --changed", "Annotate which failures touch your uncommitted edits"));
  console.log(row("cai stats", "MCP query telemetry — which files Claude actually reads"));
  console.log(row("cai check --history", "Drift score trend over time with sparkline"));
  console.log(row("cai learn review", "Find recurring corrections in your prompts (opt-in)"));
  console.log(row("cai learn watch", "Live-tail new corrections as they happen"));
  console.log(row("cai pattern suggest", "Suggest patterns from your global library"));
  console.log(row("cai pattern recurring", "Find recurring task types in commit history"));
  console.log(row("cai suggest-structure", "Deterministic scaffold improvements (overlap, hotspots, islands)"));
  console.log(row("cai heartbeat", "Cheap staleness pulse — HEARTBEAT_OK or stale files"));
  console.log(row("cai watchdog --session <id>", "Warn on high context fill + emit a condense brief"));
  console.log(row("cai log <kind> <msg>", "Append an event (decision, risk, todo, note)"));
  console.log(row("cai timeline", "View the scaffold event log, newest first"));
  console.log(row("cai session --auto", "Hook-mode session context for Claude Code"));
  console.log(row("cai visualize", "Open interactive scaffold graph in browser"));
  console.log(row("cai doctor", "Diagnose scaffold, configs, and workspace"));
  console.log(row("cai init", "Pre-scan codebase, build AI brief"));
  console.log(row("cai codex", "Generate code map (.cai/codex/modules.md + repo-brief.md)"));
  console.log();
  console.log(dim("  ─── Manage ─────────────────────────────────────────────"));
  console.log(row("cai update", "Refresh CAI infrastructure, keep your content"));
  console.log(row("cai watch", "Auto-check drift after every commit"));
  console.log(row("cai watch --auto-fix", "Auto-fix drift + refresh session context on commit"));
  console.log(row("cai watch --uninstall", "Remove the post-commit hook"));
  console.log(row("cai pattern add <name>", "Create a new pattern file"));
  console.log(row("cai pattern capture", "Auto-draft pattern from last commit"));
  console.log(row("cai pattern share <name>", "Promote a pattern to the global library"));
  console.log(row("cai pattern library", "List your global pattern library"));
  console.log(row("cai pattern install <name>", "Install a library pattern into this project"));
  console.log(row("cai sync-configs", "Re-sync all tool config files"));
  console.log();
  console.log(dim("  ─── Hooks (advanced) ───────────────────────────────────"));
  console.log(row("cai session-install-hook", "Install UserPromptSubmit hook for cai session --auto"));
  console.log(row("cai session-uninstall-hook", "Remove the cai session --auto hook"));
  console.log(row("cai verify-install-hook", "Install Stop hook for cai verify (also auto-installed)"));
  console.log(row("cai verify-uninstall-hook", "Remove the cai verify Stop hook"));
  console.log(row("cai learn install-hook", "Install UserPromptSubmit hook for cai learn record"));
  console.log(row("cai learn forget", "Wipe all recorded prompts (irreversible)"));
  console.log();
}

/** Commander accumulator for repeatable options (e.g. `--file a --file b`). */
function collectRepeatable(value: string, previous: string[]): string[] {
  return [...previous, value];
}

function parseCheckerList(input?: string): DriftCheckerName[] | undefined {
  if (!input) return undefined;
  const values = input
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  const invalid = values.filter(
    (value) => !AVAILABLE_DRIFT_CHECKERS.includes(value as (typeof AVAILABLE_DRIFT_CHECKERS)[number])
  );
  if (invalid.length > 0) {
    throw new Error(
      `Unknown checker(s): ${invalid.join(", ")}. Available: ${AVAILABLE_DRIFT_CHECKERS.join(", ")}`
    );
  }

  return values as DriftCheckerName[];
}
